import { Injectable, Injector } from '@angular/core';
import * as moment from 'moment';
import { CompanyDocument } from "../models/CompanyDocument";
import { AthleteService } from "./athlete/athlete.service";
import { CompanyDocumentService } from "./companyDocument/document.service";
import { Constants } from "../constants";
import { environment } from "../environments/environment";
import { Router } from "@angular/router";
import { HttpClient } from "@angular/common/http";
import { BehaviorSubject } from "rxjs";

@Injectable({
    providedIn: 'root'
})
export class UtilsService {

    athleteService: AthleteService;
    companyDocumentService: CompanyDocumentService;

    hasShopService: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    activeMenuSubject: BehaviorSubject<number> = new BehaviorSubject<number>(0);

    constructor(public injector: Injector, private router: Router, private http: HttpClient) {
        this.athleteService = injector.get(AthleteService);
        this.companyDocumentService = injector.get(CompanyDocumentService);
    }

    formatDateForTable(date: string) {
        return moment(date).format('DD-MM-YYYY');
    }

    formatPeriodForTable(startDate: string, endDate: string) {
        if (!startDate && !endDate) {
            return '-';
        }

        const formatDate = (date: string) => {
            return date ? moment(date).format('MM/YYYY') : '-';
        };

        return `${formatDate(startDate)} - ${formatDate(endDate)}`;
    }

    async checkAthleteMandatoryDocuments(athleteId: string) {
        let missingCompanyDocuments: CompanyDocument[] = [];
        const listOfMandatoryDocuments: CompanyDocument[] = (await this.companyDocumentService
            .getCompanyDocuments(localStorage.getItem('wisper_companyId') ?? '', athleteId))
            .filter((document: CompanyDocument) => document.isMandatory)

        let athleteCompanyDocuments = await this.athleteService.getAthleteDocuments(athleteId);

        // se l'atleta non ha documenti, gli mancano tutti i documenti obbligatori
        if (athleteCompanyDocuments.length === 0) {
            missingCompanyDocuments = [...listOfMandatoryDocuments];
            return missingCompanyDocuments;
        }

        listOfMandatoryDocuments.forEach((mandatoryDocument: CompanyDocument) => {
            if (!athleteCompanyDocuments.find((athleteDocument: CompanyDocument) => mandatoryDocument.id === athleteDocument.id)) {
                missingCompanyDocuments.push(mandatoryDocument)
            }
        })


        return missingCompanyDocuments;
    }

    async getPermissions(token: string) {
        let response: any;

        try {
            response = await this.http.get(environment.apiUrl + '/permissions', {
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    Authorization: token,
                }
            }).toPromise();
        } catch (e) {
            console.error('error getPermissions: ', e)
        } finally {
            return response;
        }
    }

    loginSuccess(response: any) {
        if (response.success) {

            localStorage.clear();

            const { data } = response;

            setTimeout(async () => {

                localStorage.setItem(Constants.LOCAL_TOKEN, data.accessToken)
                if (data.refreshToken) {
                    localStorage.setItem('wisper_refreshToken', data.refreshToken);
                }
                localStorage.setItem('wisper_firstname', data.user.firstName)
                localStorage.setItem('wisper_lastname', data.user.lastName)
                localStorage.setItem('wisper_email', data.user.email)
                localStorage.setItem('wisper_userGroup', data.user.userGroupId)
                localStorage.setItem('wisper_companyId', data.user.companyId)
                localStorage.setItem('wisper_userId', data.user.id)
                localStorage.setItem('wisper_service', data.companyServices)
                localStorage.setItem('app', environment.appName);

                if (data.onboarding) {
                    localStorage.setItem('register-company-onboarding-token', data.onboarding.resumeToken);
                    localStorage.setItem('register-company-step', String(data.onboarding.currentStep ?? 1));
                    localStorage.setItem('register-company-data', JSON.stringify({
                        newCompany: {
                            legalEmail: data.user.email,
                            ...(data.onboarding.data?.representative ?? {}),
                            ...(data.onboarding.data?.contacts ?? {}),
                            ...(data.onboarding.data?.companyInfo ?? {}),
                        },
                        companyTermsAndCondition1: data.onboarding.data?.terms?.termsAccepted === true,
                        companyTermsAndCondition2: data.onboarding.data?.terms?.privacyAccepted === true,
                        companyTermsAndCondition3: data.onboarding.data?.terms?.clausesAccepted === true,
                    }));
                }

                if (!data.onboarding) {
                    let permissionResponse = await this.getPermissions(data.accessToken);

                    if (permissionResponse?.success) {
                        localStorage.setItem('wisper_permissions', permissionResponse.data
                            .map((permission: any) => permission.id)
                        );
                    }
                }
            }, 200);

            // timeout perché sennò al redirect, per qualche motivo usa ancora il token vecchio dando errore 401
            setTimeout(() => {
                if (data.onboarding) {
                    this.router.navigate(['/register/companies']);
                    return;
                }
                if (data.user.userGroupId === 'admin') this.router.navigate(['/admins/dashboard']);
                if (data.user.userGroupId === 'companyManager') this.router.navigate(this.getCompanyManagerHomeRoute());
                if (data.user.userGroupId === 'user') this.router.navigate(['/users/dashboard']);
                if (data.user.userGroupId === 'agent') this.router.navigate(['/agents/dashboard']);
                if (data.user.userGroupId === 'mister') this.router.navigate(['/misters/dashboard']);
            }, 300)
        }
    }

    remaningDays(date: string): { status: string, text: string } {
        if (!date) return {
            text: 'Non disponibile',
            status: 'unknown'
        };
        const today = new Date();
        const expirationDate = new Date(date);
        const diffTime = expirationDate.getTime() - today.getTime();
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        if (diffDays > 30) {
            return {
                text: `Ok (scade il ${moment(date).format('DD/MM/YYYY')})`,
                status: 'success'
            };
        } else if (diffDays <= 30 && diffDays > 0) {
            return {
                text: `In scadenza (tra ${diffDays} giorni)`,
                status: 'warning'
            };
        } else {
            return {
                text: `Scaduto da ${Math.abs(diffDays)} giorni`,
                status: 'danger'
            }
        }

    }

    /**
     * Decode JWT payload without verification (client-side only).
     * Uses token from localStorage if token not passed.
     * Decodes payload as UTF-8 so values like "Gestore società" are correct (not "societÃ ").
     */
    decodeJwtPayload(token?: string): Record<string, unknown> | null {
        try {
            const raw = token ?? localStorage.getItem(Constants.LOCAL_TOKEN);
            if (!raw) return null;
            const parts = raw.split('.');
            if (parts.length !== 3) return null;
            const base64Url = parts[1];
            const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
            const padded = base64 + '=='.slice(0, (4 - base64.length % 4) % 4);
            const binary = atob(padded);
            const bytes = new Uint8Array(binary.length);
            for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
            const json = new TextDecoder('utf-8').decode(bytes);
            return JSON.parse(json) as Record<string, unknown>;
        } catch {
            return null;
        }
    }

    /**
     * User group from JWT payload (e.g. "Admin" -> "admin") for immediate navbar/auth use.
     * Fallback: localStorage wisper_userGroup.
     */
    getJwtUserGroup(): string {
        const payload = this.decodeJwtPayload();
        const fromToken = payload?.['userGroup'];
        if (typeof fromToken === 'string' && fromToken.length > 0) {
            return fromToken.toLowerCase();
        }
        return localStorage.getItem('wisper_userGroup') ?? '';
    }

    /**
     * Company services from JWT payload (e.g. { "1": "Dashboard", "2": "Subscription", ... }).
     * Fallback: localStorage wisper_service. Used for agents without API call.
     */
    getJwtCompanyServices(): Record<string, string> | null {
        const payload = this.decodeJwtPayload();
        const fromToken = payload?.['companyServices'];
        if (fromToken && typeof fromToken === 'object' && !Array.isArray(fromToken)) {
            return fromToken as Record<string, string>;
        }
        const stored = localStorage.getItem('wisper_service');
        if (stored) {
            try {
                return JSON.parse(stored) as Record<string, string>;
            } catch {
                return null;
            }
        }
        return null;
    }

    getJwtCompanyId(): string {
        const payload = this.decodeJwtPayload();
        const fromToken = payload?.['companyId'];
        if (fromToken && typeof fromToken === 'string') {
            return fromToken;
        }
        return localStorage.getItem('wisper_companyId') ?? '';
    }

    /**
     * Company status from JWT payload (e.g. "active", "paymentFailed", "pendingPayment").
     */
    getJwtCompanyStatus(): string | null {
        const payload = this.decodeJwtPayload();
        const fromToken = payload?.['companyStatus'];
        console.log('fromToken', fromToken);
        if (typeof fromToken === 'string' && fromToken.length > 0) {
            return fromToken;
        }
        return null;
    }

    /**
     * Permissions from JWT payload (array of permission ids).
     * Fallback: localStorage wisper_permissions (comma-separated).
     */
    getJwtPermissions(): string[] {
        const payload = this.decodeJwtPayload();
        const fromToken = payload?.['permissions'];
        if (Array.isArray(fromToken)) {
            return fromToken.filter((p): p is string => typeof p === 'string');
        }
        if (typeof fromToken === 'string') {
            return fromToken ? fromToken.split(',').map(s => s.trim()).filter(Boolean) : [];
        }
        const stored = localStorage.getItem('wisper_permissions');
        return stored ? stored.split(',').map(s => s.trim()).filter(Boolean) : [];
    }

    /**
     * Company features from JWT payload (enabled subscription features).
     * If null/undefined or empty object, the user has no subscription enabled.
     */
    getJwtCompanyFeature(): Record<string, unknown> | null {
        const payload = this.decodeJwtPayload();
        const fromToken = payload?.['companyFeature'];
        if (fromToken !== null && fromToken !== undefined && typeof fromToken === 'object' && !Array.isArray(fromToken)) {
            return fromToken as Record<string, unknown>;
        }
        return null;
    }

    /**
     * True when the JWT indicates an active subscription.
     * Priority:
     * 1) companyFeature object with at least one key
     * 2) companyServices object with at least one key
     */
    hasActiveSubscription(): boolean {
        const feature = this.getJwtCompanyFeature();
        if (feature && Object.keys(feature).length > 0) {
            return true;
        }

        const services = this.getJwtCompanyServices();
        return !!services && Object.keys(services).length > 0;
    }

    /** True when JWT companyServices contains the given key (case-insensitive). */
    canAccessJwtService(serviceKey: string): boolean {
        if (!serviceKey) return false;
        const services = this.getJwtCompanyServices();
        if (!services) return false;
        const keyLower = serviceKey.toLowerCase();
        return Object.keys(services).some((k) => k.toLowerCase() === keyLower);
    }

    /** Default landing route for company managers based on enabled services. */
    getCompanyManagerHomeRoute(): string[] {
        if (this.canAccessJwtService('dashboard')) {
            return ['/companies/dashboard'];
        }
        if (this.canAccessJwtService('onlineshop')) {
            return ['/companies/shop/orders'];
        }
        return ['/companies/dashboard'];
    }

    getCurrentUser() {
        return localStorage.getItem('wisper_userId')
    }

    isSuperAdmin() {
        return this.getJwtUserGroup() === 'admin';
    }

    isAgent() {
        return this.getJwtUserGroup() === 'agent';
    }

    isEmpty(str: any) {
        if (!str || str == null || str == '') {
            return true;
        }
        return false;
    }
}
